import { useQuery } from "@tanstack/react-query";
import { Bell, BookOpen, Check, Clock3, Database, KeyRound, Layers, PlugZap, Shield, Sparkles, Trash2, Users } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { GenerationJob, ProviderKeyStatus, TaxonomyAxis, WorkspaceSettings } from "@lovmia/shared";
import { roleLabels, taxonomyAxes } from "@lovmia/shared";
import { api, formatMoney, relativeTime } from "../api";
import { Button, PageHeader, Select, Tag } from "../components/Ui";
import { NavLink } from "../router";
import { useAppState } from "../state";

function NumberField({ label, value, onChange, suffix, step = 1 }: { label: string; value: number; onChange: (value: number) => void; suffix?: string; step?: number }) {
  return <label className="setting-field"><span>{label}</span><div><input type="number" step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} />{suffix && <b>{suffix}</b>}</div></label>;
}

const jobStatusLabels: Record<GenerationJob["status"], string> = {
  queued: "в очереди", processing: "генерится", completed: "готово", failed: "ошибка"
};

/**
 * Ключ WaveSpeed и расход по нему (GEN-1180).
 *
 * Введённый ключ уходит на сервер и больше сюда не возвращается: после
 * сохранения видна только подсказка `wsk_live_…XQ1g`. Поэтому поле ввода
 * всегда пустое — редактировать нечего, можно лишь заменить ключ целиком.
 *
 * Секция живёт внутри общей формы настроек, но своими запросами: пороги
 * сохраняются одной кнопкой внизу, а ключ — отдельным действием, и путать эти
 * два сохранения нельзя. Отсюда type="button" на каждой кнопке.
 */
function GenerationSection({ settings, onChange }: { settings: WorkspaceSettings; onChange: (settings: WorkspaceSettings) => void }) {
  const { notify } = useAppState();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const status = useQuery({
    queryKey: ["wavespeed-key"],
    queryFn: () => api<ProviderKeyStatus>("/api/settings/integrations/wavespeed")
  });
  const jobs = useQuery({
    queryKey: ["wavespeed-jobs"],
    queryFn: () => api<{ items: GenerationJob[] }>("/api/gen/wavespeed/jobs?limit=10"),
    staleTime: 15_000
  });

  const guard = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      notify("Не вышло", error instanceof Error ? error.message : undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const saveKey = () => guard(async () => {
    await api("/api/settings/integrations/wavespeed", { method: "PUT", body: JSON.stringify({ apiKey: draft.trim() }) });
    setDraft("");
    await status.refetch();
    notify("Ключ WaveSpeed сохранён", "Проверен запросом баланса", "success");
  });

  const removeKey = () => guard(async () => {
    await api("/api/settings/integrations/wavespeed", { method: "DELETE" });
    await status.refetch();
    notify("Ключ удалён", "Генерация через MCP больше не работает", "success");
  });

  const checkBalance = () => guard(async () => {
    const { balance } = await api<{ balance: number }>("/api/settings/integrations/wavespeed/balance");
    notify("WaveSpeed отвечает", `На счёте ${formatMoney(balance, 2)}`, "success");
  });

  const key = status.data;
  return <div className="settings-section settings-section--stack">
    <div>
      <span className="eyebrow">provider key · admin only</span>
      <h2>Генерация на WaveSpeed</h2>
      <p>Ключ хранится зашифрованным на сервере: Claude генерирует через MCP, а сам ключ не видит ни он, ни команда. Расход общий, поэтому дневной потолок — единственный ограничитель.</p>
    </div>

    {key && !key.storageReady && <p className="settings-warning">На сервере не задан <code>SECRETS_KEY</code> — шифровать ключ нечем, поэтому завести его нельзя. Добавьте переменную окружения и перезапустите API.</p>}

    <div className="integration-key">
      <label className="setting-field">
        <span>{key?.configured ? "Заменить ключ" : "API-ключ WaveSpeed"}</span>
        <div>
          <input
            type="password"
            autoComplete="off"
            placeholder={key?.configured ? key.hint ?? "" : "wsk_live_…"}
            value={draft}
            disabled={!key?.storageReady || busy}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
      </label>
      <div className="integration-key__actions">
        <Button type="button" variant="primary" disabled={draft.trim().length < 16 || busy} onClick={saveKey}>
          <KeyRound size={15} /> Сохранить ключ
        </Button>
        {key?.configured && <>
          <Button type="button" onClick={checkBalance} disabled={busy}>Проверить баланс</Button>
          <Button type="button" variant="danger" onClick={removeKey} disabled={busy}><Trash2 size={15} /> Удалить</Button>
        </>}
      </div>
      {key?.configured
        ? <small>Заведён {relativeTime(key.updatedAt)} · <code>{key.hint}</code></small>
        : <small>Ключ ещё не заведён — тулзы генерации в чате будут отвечать отказом.</small>}
    </div>

    <div className="settings-grid settings-grid--2">
      <NumberField
        label="Дневной лимит генерации"
        value={settings.generation.dailyLimitUsd}
        suffix="$"
        onChange={(dailyLimitUsd) => onChange({ ...settings, generation: { ...settings.generation, dailyLimitUsd } })}
      />
    </div>
    <p className="settings-hint">Ноль выключает генерацию совсем. Расход считается оценкой по прайсу модели — она ниже фактической для длинных и 4K-роликов, поэтому потолок стоит держать с запасом.</p>

    <div className="generation-log">
      <h3>Последние генерации</h3>
      {!jobs.data?.items.length
        ? <p className="settings-hint">Пока ничего не генерировали.</p>
        : <div className="team-list">{jobs.data.items.map((job) => <div key={job.id}>
          <div>
            <strong>{job.modelId}</strong>
            <small>{job.actorName ?? (job.source === "mcp" ? "Claude" : "система")} · {relativeTime(job.createdAt)}{job.error ? ` · ${job.error}` : ""}</small>
          </div>
          <b>{formatMoney(job.priceEstimate, 2)}</b>
          <Tag tone={job.status === "failed" ? "pain" : job.status === "completed" ? "accent" : "default"}>{jobStatusLabels[job.status]}</Tag>
          {job.files[0] && <a className="button button--ghost" href={job.files[0].url} target="_blank" rel="noreferrer">Открыть</a>}
        </div>)}</div>}
    </div>
  </div>;
}

/**
 * Slack-уведомления (GEN-1600). Токен бота устроен как ключ WaveSpeed: уходит
 * на сервер и обратно не возвращается, после сохранения видна только подсказка.
 * Канал — обычная настройка, сохраняется общей кнопкой формы.
 */
function SlackSection({ settings, onChange }: { settings: WorkspaceSettings; onChange: (settings: WorkspaceSettings) => void }) {
  const { notify } = useAppState();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const status = useQuery({
    queryKey: ["slack-key"],
    queryFn: () => api<ProviderKeyStatus>("/api/settings/integrations/slack")
  });

  const guard = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      notify("Не вышло", error instanceof Error ? error.message : undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const saveToken = () => guard(async () => {
    await api("/api/settings/integrations/slack", { method: "PUT", body: JSON.stringify({ botToken: draft.trim() }) });
    setDraft("");
    await status.refetch();
    notify("Токен Slack сохранён", "Проверен запросом auth.test", "success");
  });

  const removeToken = () => guard(async () => {
    await api("/api/settings/integrations/slack", { method: "DELETE" });
    await status.refetch();
    notify("Токен удалён", "Уведомления в Slack больше не отправляются", "success");
  });

  const key = status.data;
  return <div className="settings-section settings-section--stack">
    <div>
      <span className="eyebrow">slack · admin only</span>
      <h2>Уведомления в Slack</h2>
      <p>Бот пишет в канал, когда гипотеза дотестировалась и её пора обработать. Токен хранится зашифрованным на сервере; пустой канал выключает отправку.</p>
    </div>

    {key && !key.storageReady && <p className="settings-warning">На сервере не задан <code>SECRETS_KEY</code> — шифровать токен нечем, поэтому завести его нельзя. Добавьте переменную окружения и перезапустите API.</p>}

    <div className="integration-key">
      <label className="setting-field">
        <span>{key?.configured ? "Заменить токен бота" : "Токен Slack-бота"}</span>
        <div>
          <input
            type="password"
            autoComplete="off"
            placeholder={key?.configured ? key.hint ?? "" : "xoxb-…"}
            value={draft}
            disabled={!key?.storageReady || busy}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
      </label>
      <div className="integration-key__actions">
        <Button type="button" variant="primary" disabled={draft.trim().length < 16 || busy} onClick={saveToken}>
          <KeyRound size={15} /> Сохранить токен
        </Button>
        {key?.configured && <Button type="button" variant="danger" onClick={removeToken} disabled={busy}><Trash2 size={15} /> Удалить</Button>}
      </div>
      {key?.configured
        ? <small>Заведён {relativeTime(key.updatedAt)} · <code>{key.hint}</code></small>
        : <small>Токен ещё не заведён — уведомления не отправляются.</small>}
    </div>

    <div className="settings-grid settings-grid--2">
      <label className="setting-field">
        <span>Канал</span>
        <div>
          <input
            value={settings.slack.channel}
            placeholder="C0BUGQ939AP"
            onChange={(event) => onChange({ ...settings, slack: { ...settings.slack, channel: event.target.value } })}
          />
        </div>
      </label>
    </div>
    <p className="settings-hint">Бота нужно пригласить в канал, иначе Slack ответит not_in_channel и сообщение не дойдёт.</p>
  </div>;
}

/**
 * Справочник продуктов (GEN-1709): на какие приложения льются крео. Закрытый
 * список вместо свободной строки — опечатка развела бы один продукт на два
 * фильтра доски. Сохраняется общей кнопкой формы, как остальные настройки.
 */
function ProductsSection({ settings, onChange }: { settings: WorkspaceSettings; onChange: (settings: WorkspaceSettings) => void }) {
  const [draft, setDraft] = useState("");
  const name = draft.trim();
  const addProduct = () => {
    if (name.length < 2 || settings.products.includes(name)) return;
    onChange({ ...settings, products: [...settings.products, name] });
    setDraft("");
  };
  return <div className="settings-section settings-section--stack">
    <div><span className="eyebrow">products</span><h2>Продукты</h2>
      <p>Каждая гипотеза принадлежит продукту — приложению, на которое льются её крео. По продукту фильтруется доска и делится статистика в админке. Первый в списке — дефолт новых гипотез.</p></div>
    <div className="team-list">{settings.products.map((product) => <div key={product}>
      <div><strong>{product}</strong></div>
      <Button type="button" variant="ghost" disabled={settings.products.length <= 1}
        onClick={() => onChange({ ...settings, products: settings.products.filter((item) => item !== product) })}>
        <Trash2 size={14} /> Убрать
      </Button>
    </div>)}</div>
    <div className="settings-grid settings-grid--2">
      <label className="setting-field"><span>Новый продукт</span><div>
        <input value={draft} placeholder="Lovmia Shorts" maxLength={64} onChange={(event) => setDraft(event.target.value)} />
      </div></label>
    </div>
    <Button type="button" variant="primary" disabled={name.length < 2 || settings.products.includes(name)} onClick={addProduct}><Check size={15} /> Добавить продукт</Button>
    <p className="settings-hint">Удаление из справочника не переписывает старые гипотезы — их продукт остаётся видимым в фильтре, но новые гипотезы с ним не заводятся.</p>
  </div>;
}

const axisLabels: Record<TaxonomyAxis, string> = {
  pain: "Боли", desire: "Желания", angle: "Углы", tone: "Тон", awareness: "Осведомлённость",
  hook: "Хуки", delivery: "Подача", pacing: "Темп", pillar: "Пиллары", format: "Форматы",
  producer: "Продюсеры", source: "Источники"
};

/**
 * Словарь таксономии. Раньше был read-only рендером константы из кода; теперь
 * термины живут в базе, и здесь же разбираются предложения из ресерча —
 * состав словаря меняет админ, а не деплой.
 */
function DictionarySection({ isAdmin }: { isAdmin: boolean }) {
  const { data, act } = useAppState();
  const [axis, setAxis] = useState<TaxonomyAxis>("pain");
  const [draft, setDraft] = useState({ slug: "", title: "", adCode: "", description: "", angleHint: "" });
  const terms = data.taxonomy.terms.filter((term) => term.axis === axis);
  const proposals = data.taxonomy.terms.filter((term) => term.status === "proposed");

  // Кнопка живёт внутри общей формы настроек, поэтому type="button" и обычный
  // вызов: сохранение порогов и правка словаря — разные действия.
  const addTerm = () => {
    return act("/api/taxonomy/terms", {
      method: "POST",
      body: JSON.stringify({
        ...draft, axis, slug: draft.slug.trim(),
        // Код нужен только боли: слаг остальных осей либо не уезжает в ad_name,
        // либо стоит в нём буквально и уже безопасен.
        adCode: axis === "pain" ? draft.adCode.trim() : undefined,
        rationale: null, evidence: []
      })
    }, `«${draft.slug.trim()}» в словаре`).then(() => setDraft({ slug: "", title: "", adCode: "", description: "", angleHint: "" }));
  };

  return <div className="settings-section settings-section--stack">
    <div><span className="eyebrow">taxonomy v{data.taxonomy.version}</span><h2>Словарь</h2>
      <p>Истина — база, а не код. Одна боль на гипотезу: утверждение формулируется от боли к желанию. Ресерч предлагает новый термин с обоснованием через MCP, принимают его здесь или в Разборе.</p>
      <p className="settings-hint">Слаг боли — наш внутренний язык, он называет состояние прямо. В имя объявления вместо него уезжает «код в кабинете»: имя видит модерация площадки, и слова про уязвимость человека там стоят аккаунту.</p></div>

    {proposals.length > 0 && <div className="term-proposals">
      <div className="section-heading"><span>Предложено ресерчем</span><b>{proposals.length}</b></div>
      {proposals.map((term) => <article className="term-proposal" key={`${term.axis}:${term.slug}`}>
        <div className="term-proposal__head"><Tag tone="pain">{term.axis}</Tag><span className="mono">{term.slug}</span><strong>{term.title}</strong>{term.adCode && <Tag>в кабинете: {term.adCode}</Tag>}</div>
        {term.description && <p>{term.description}</p>}
        {term.rationale && <small><b>Обоснование:</b> {term.rationale}</small>}
        <small>{term.proposedByName ? `Предложил ${term.proposedByName}` : "Предложение из чата"}{term.evidence.length ? ` · ${term.evidence.join(" · ")}` : ""}</small>
        {isAdmin && <div className="term-proposal__actions">
          <Button type="button" variant="primary" onClick={() => act(`/api/taxonomy/proposals/${term.axis}/${term.slug}/approve`, { method: "POST" }, `«${term.slug}» в словаре`)}><Check size={14} /> Принять</Button>
          <Button type="button" variant="ghost" onClick={() => act(`/api/taxonomy/proposals/${term.axis}/${term.slug}/reject`, { method: "POST" }, "Предложение отклонено")}>Отклонить</Button>
        </div>}
      </article>)}
    </div>}

    <div className="axis-switch">{taxonomyAxes.map((item) => <button type="button" key={item} className={item === axis ? "active" : ""} onClick={() => setAxis(item)}>{axisLabels[item]}</button>)}</div>

    <div className="pain-guide">
      {terms.filter((term) => term.status !== "proposed").map((term) => <article className={`pain-row ${term.status === "retired" ? "pain-row--retired" : ""}`} key={term.slug}>
        <div className="pain-row__head"><Tag tone="pain">{term.slug}</Tag><strong>{term.title}</strong>
          {term.adCode && <Tag>в кабинете: {term.adCode}</Tag>}
          {term.status === "retired" && <Tag>выведен из оборота</Tag>}
          {isAdmin && <Button type="button" variant="ghost" onClick={() => act(`/api/taxonomy/terms/${term.axis}/${term.slug}`, {
            method: "PATCH", body: JSON.stringify({ status: term.status === "retired" ? "active" : "retired" })
          }, term.status === "retired" ? `«${term.slug}» снова в обороте` : `«${term.slug}» выведен из оборота`)}>{term.status === "retired" ? "Вернуть" : "Вывести"}</Button>}
        </div>
        {term.description && <p>{term.description}</p>}
        {term.angleHint && <small><b>Заход в крео:</b> {term.angleHint}</small>}
      </article>)}
      {!terms.length && <p className="settings-hint">В этой оси пока пусто.</p>}
    </div>

    {isAdmin && <div className="term-form">
      <div className="section-heading"><span>Добавить в «{axisLabels[axis].toLowerCase()}»</span></div>
      <p className="settings-hint">Норма — примерно одно расширение на десять крео. Если термин просится чаще, скорее всего это переформулировка существующего.</p>
      <div className="settings-grid settings-grid--2">
        <label className="setting-field"><span>Слаг</span><div><input value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: event.target.value })} placeholder="dating_fatigue" /></div></label>
        <label className="setting-field"><span>Название</span><div><input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Усталость от дейтинга" /></div></label>
      </div>
      {axis === "pain" && <label className="setting-field"><span>Код в кабинете</span>
        <div><input value={draft.adCode} onChange={(event) => setDraft({ ...draft, adCode: event.target.value })} placeholder="swipe_time" /></div>
        <p className="settings-hint">Этим словом боль входит в ad_name — его читает модерация площадки. Нейтрально и о теме крео, а не о состоянии человека.</p>
      </label>}
      <label className="setting-field"><span>Описание</span><div><input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="Что человек чувствует" /></div></label>
      <label className="setting-field"><span>Заход в крео</span><div><input value={draft.angleHint} onChange={(event) => setDraft({ ...draft, angleHint: event.target.value })} placeholder="Как боль выглядит в ролике" /></div></label>
      <Button type="button" variant="primary" disabled={draft.slug.trim().length < 2 || draft.title.trim().length < 2 || (axis === "pain" && draft.adCode.trim().length < 2)} onClick={addTerm}><Check size={15} /> Добавить термин</Button>
    </div>}
  </div>;
}

export function SettingsPage() {
  const { data, act } = useAppState();
  const [settings, setSettings] = useState<WorkspaceSettings>(data.settings);
  const [tab, setTab] = useState("rules");
  // Ключ провайдера — админская вкладка целиком: ручки под ней отвечают 403
  // пользователю, и показывать ему пустой раздел незачем.
  const isAdmin = data.user.role === "admin";
  const save = (event: FormEvent) => { event.preventDefault(); return act("/api/settings", { method: "PATCH", body: JSON.stringify(settings) }, "Настройки сохранены"); };
  // Один знак после запятой, а не целые: порог hold rate 8.5% иначе показался бы девяткой.
  const percent = (value: number) => Math.round(value * 1000) / 10;
  return <>
    <PageHeader eyebrow="control room" title="Настройки контура" description="Пороги — это политика команды, а не константы в коде." />
    <div className="settings-layout"><aside className="settings-nav"><button className={tab === "rules" ? "active" : ""} onClick={() => setTab("rules")}><Shield size={16} /> Гейты и вердикты</button><button className={tab === "medians" ? "active" : ""} onClick={() => setTab("medians")}><Database size={16} /> Своя линейка</button><button className={tab === "team" ? "active" : ""} onClick={() => setTab("team")}><Users size={16} /> Команда и роли</button><button className={tab === "rituals" ? "active" : ""} onClick={() => setTab("rituals")}><Clock3 size={16} /> Ритуалы</button><button className={tab === "dictionary" ? "active" : ""} onClick={() => setTab("dictionary")}><BookOpen size={16} /> Словарь</button>{isAdmin && <button className={tab === "products" ? "active" : ""} onClick={() => setTab("products")}><Layers size={16} /> Продукты</button>}{isAdmin && <button className={tab === "generation" ? "active" : ""} onClick={() => setTab("generation")}><Sparkles size={16} /> Генерация</button>}{isAdmin && <button className={tab === "slack" ? "active" : ""} onClick={() => setTab("slack")}><Bell size={16} /> Уведомления</button>}<NavLink to="/pipeline"><PlugZap size={16} /> Инструкция</NavLink></aside>
      <form className="settings-panel" onSubmit={save}>
        {tab === "rules" && <><div className="settings-section"><div><span className="eyebrow">spend policy</span><h2>Когда данных достаточно</h2><p>До основного гейта крео не получает финальный диагноз. Emergency stop защищает бюджет при сломанном входе. Бюджет теста 2 — сколько дельты спенда набирает скейл-прогон до «Вердикт (тест 2)».</p></div><div className="settings-grid settings-grid--3"><NumberField label="Основной гейт" value={settings.gateSpend} suffix="$" onChange={(gateSpend) => setSettings({ ...settings, gateSpend })} /><NumberField label="Emergency stop" value={settings.emergencyStopSpend} suffix="$" onChange={(emergencyStopSpend) => setSettings({ ...settings, emergencyStopSpend })} /><NumberField label="Бюджет теста 2" value={settings.test2Spend} suffix="$" onChange={(test2Spend) => setSettings({ ...settings, test2Spend })} /></div></div><div className="settings-section"><div><span className="eyebrow">absolute thresholds</span><h2>Пороги оценки крео</h2><p>Hook и hold судятся абсолютными порогами: значение не ниже порога — зелёное, ниже — красное и слом. Победитель — тест завершён (гейт пройден) и CPS ниже порога: вердикт подсвечивается на карточке, а в «Виннеры» крео переводит человек.</p></div><div className="settings-grid settings-grid--3"><NumberField label="Порог hook rate" value={percent(settings.hookRateMin)} suffix="%" step={.1} onChange={(value) => setSettings({ ...settings, hookRateMin: value / 100 })} /><NumberField label="Порог hold rate" value={percent(settings.holdRateMin)} suffix="%" step={.1} onChange={(value) => setSettings({ ...settings, holdRateMin: value / 100 })} /><NumberField label="Winner CPS" value={settings.winnerCps} suffix="$" onChange={(winnerCps) => setSettings({ ...settings, winnerCps })} /></div></div><div className="settings-section"><div><span className="eyebrow">relative verdicts</span><h2>Слом клоуза от медианы</h2><p>CTR судится относительно своей истории: у клоуза нет договорённого абсолютного порога.</p></div><div className="settings-grid"><NumberField label="Слом показателя" value={percent(settings.brokenRatio)} suffix="%" onChange={(value) => setSettings({ ...settings, brokenRatio: value / 100 })} /></div></div><div className="settings-section"><div><span className="eyebrow">moderation</span><h2>Модерация</h2><p>Счётчик «реджекты / запуски» на карточке считается по всем кабинетам. «Не используем» — доля отклонённых объявлений выше порога; пока запусков меньше минимума, доля не судится: один реджект из одного запуска ещё не 100%. «Петушиный угол» — третий реджект: его ставит Creative Quarantine в админке маркетинга, порог живёт там.</p></div><div className="settings-grid settings-grid--2"><NumberField label="Не используем: доля реджектов выше" value={percent(settings.moderation.banRate)} suffix="%" onChange={(value) => setSettings({ ...settings, moderation: { ...settings.moderation, banRate: value / 100 } })} /><NumberField label="Минимум запусков для доли" value={settings.moderation.minLaunches} onChange={(minLaunches) => setSettings({ ...settings, moderation: { ...settings.moderation, minLaunches: Math.max(1, Math.round(minLaunches)) } })} /></div></div><div className="settings-section"><div><span className="eyebrow">board layout</span><h2>Локали на доске</h2><p>Локаль — отдельное объявление в кабинете, но не отдельное измерение гипотезы. Карточками она стоит в колонке своего состояния под своим крео: «ES-MX без файла» видно из «Продакшна», а не только из карточки. Выключенная настройка возвращает прежнее чтение — флажки локалей внутри карточки крео. На гейт, вердикты и цифры переключатель не влияет: спенд локалей в гейт гипотезы не входит в обоих режимах.</p><label className="toggle"><input type="checkbox" checked={settings.flatLocalizations} onChange={(event) => setSettings({ ...settings, flatLocalizations: event.target.checked })} /><i /><span>Локали карточками на доске</span></label></div></div></>}
        {tab === "medians" && <div className="settings-section"><div><span className="eyebrow">workspace medians</span><h2>Внутренняя система координат</h2><p>На старте значения ручные. После достаточной истории их можно пересчитывать по окну последних тестов.</p><label className="toggle"><input type="checkbox" checked={settings.medians.manual} onChange={(event) => setSettings({ ...settings, medians: { ...settings.medians, manual: event.target.checked } })} /><i /><span>Фиксировать медианы вручную</span></label></div><div className="settings-grid settings-grid--2"><NumberField label="Hook rate" value={percent(settings.medians.hookRate)} suffix="%" step={.1} onChange={(value) => setSettings({ ...settings, medians: { ...settings.medians, hookRate: value / 100 } })} /><NumberField label="Hold rate" value={percent(settings.medians.holdRate)} suffix="%" step={.1} onChange={(value) => setSettings({ ...settings, medians: { ...settings.medians, holdRate: value / 100 } })} /><NumberField label="CTR" value={percent(settings.medians.ctr)} suffix="%" step={.1} onChange={(value) => setSettings({ ...settings, medians: { ...settings.medians, ctr: value / 100 } })} /><NumberField label="CPA" value={settings.medians.cpa} suffix="$" onChange={(value) => setSettings({ ...settings, medians: { ...settings.medians, cpa: value } })} /></div></div>}
        {tab === "team" && <div className="settings-section settings-section--stack"><div><span className="eyebrow">role based workspace</span><h2>Кто что видит и решает</h2><p>Админ решает и настраивает контур, пользователь работает с крео. Один аккаунт используется и в приложении, и при OAuth-подключении официального чата.</p></div><div className="team-list">{data.team.map((user) => <div key={user.id}><span className="avatar">{user.avatar}</span><div><strong>{user.name}</strong><small>{user.email}</small></div><b>{roleLabels[user.role]}</b><span className="team-ok"><Check size={13} /> активен</span></div>)}</div><NavLink className="button button--secondary" to="/team"><Users size={15} /> Управление учётками</NavLink></div>}
        {tab === "rituals" && <div className="settings-section"><div><span className="eyebrow">weekly cadence</span><h2>Два якоря недели</h2><p>Понедельник собирает волну, пятница превращает результаты в решения и learnings.</p></div><div className="settings-grid settings-grid--2"><label className="setting-field"><span>Понедельник · план</span><div><input type="time" value={settings.digestMonday} onChange={(event) => setSettings({ ...settings, digestMonday: event.target.value })} /></div></label><label className="setting-field"><span>Пятница · разбор</span><div><input type="time" value={settings.digestFriday} onChange={(event) => setSettings({ ...settings, digestFriday: event.target.value })} /></div></label><NumberField label="Explore share" value={percent(settings.exploreShare)} suffix="%" onChange={(value) => setSettings({ ...settings, exploreShare: value / 100 })} /></div></div>}
        {tab === "dictionary" && <DictionarySection isAdmin={isAdmin} />}
        {tab === "products" && isAdmin && <ProductsSection settings={settings} onChange={setSettings} />}
        {tab === "generation" && isAdmin && <GenerationSection settings={settings} onChange={setSettings} />}
        {tab === "slack" && isAdmin && <SlackSection settings={settings} onChange={setSettings} />}
        <footer className="settings-save"><span>Изменения попадут в следующие автоматические вердикты.</span><Button type="submit" variant="primary"><Check size={15} /> Сохранить</Button></footer>
      </form></div>
  </>;
}
